import React, { useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    LinearProgress, Radio, RadioGroup, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TextField, Typography,
} from '@mui/material';
import { UploadFile as UploadFileIcon } from '@mui/icons-material';
import { ImportResult, ImportSheetPreview } from '../../../types/execution';
import executionService from '../../../services/executionService';
import { errorMessage, formatDate } from './executionUtils';

interface Props {
    open: boolean;
    projectId: number;
    onClose: () => void;
    onImported: () => void;
}

const KIND_LABELS: Record<ImportSheetPreview['kind'], string> = {
    schedule: 'Cronograma', text: 'Texto (observação)', daily: 'Tarefas (Daily)', executive: 'Visão executiva', unknown: 'Não reconhecida',
};
const IMPORTABLE = ['schedule', 'text', 'daily'];

/** Importação da planilha de controle (modelo por squads): prévia, escolha das abas e gravação. */
const ImportDialog: React.FC<Props> = ({ open, projectId, onClose, onImported }) => {
    const [file, setFile] = useState<File | null>(null);
    const [sheets, setSheets] = useState<ImportSheetPreview[]>([]);
    const [selected, setSelected] = useState<Record<string, { checked: boolean; name: string }>>({});
    const [mode, setMode] = useState<'update' | 'replace'>('update');
    const [importTasks, setImportTasks] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<ImportResult | null>(null);

    const reset = () => {
        setFile(null); setSheets([]); setSelected({}); setError(null); setResult(null); setMode('update'); setImportTasks(true);
    };

    const close = () => { reset(); onClose(); };

    const pickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const chosen = e.target.files?.[0] || null;
        e.target.value = '';
        if (!chosen) return;
        setError(null); setResult(null); setFile(chosen); setBusy(true);
        try {
            const preview = await executionService.previewImport(projectId, chosen);
            setSheets(preview.sheets);
            // Nome padrão da frente = nome da aba (sem o sufixo entre parênteses)
            setSelected(Object.fromEntries(preview.sheets.map((s) => [s.title, {
                checked: s.suggested, name: s.title.replace(/\s*\(.*?\)\s*/g, ' ').trim(),
            }])));
        } catch (err) {
            setError(errorMessage(err, 'Não foi possível ler a planilha'));
            setSheets([]);
        } finally {
            setBusy(false);
        }
    };

    const chosenSheets = sheets.filter((s) => selected[s.title]?.checked);
    // Duas abas apontando para a mesma frente (ex.: "DIMP" e "DIMP (Ajuste MSA)") se sobrescreveriam
    const names = chosenSheets.filter((s) => s.kind !== 'daily').map((s) => selected[s.title].name.trim().toLowerCase());
    const duplicated = names.filter((n, i) => names.indexOf(n) !== i);

    const runImport = async () => {
        if (!file) return;
        setBusy(true); setError(null);
        try {
            const res = await executionService.runImport(projectId, file,
                chosenSheets.map((s) => ({ title: s.title, name: selected[s.title].name.trim() })), mode, importTasks);
            setResult(res);
            onImported();
        } catch (err) {
            setError(errorMessage(err, 'Erro na importação'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
            <DialogTitle>Importar planilha de controle</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Cada aba de squad vira uma frente (fases, atividades, % real, datas e responsáveis); abas só de texto
                    viram a observação de status da frente; a aba "Daily" vira tarefas. Antes de alterar uma frente que já
                    existe, o sistema guarda uma linha de base com o plano anterior.
                </Typography>

                <Box my={2}>
                    <input id="control-xlsx" type="file" accept=".xlsx" style={{ display: 'none' }} onChange={pickFile} />
                    <label htmlFor="control-xlsx">
                        <Button component="span" variant="outlined" startIcon={<UploadFileIcon />} disabled={busy}>
                            {file ? 'Trocar planilha' : 'Escolher planilha (.xlsx)'}
                        </Button>
                    </label>
                    {file && <Typography variant="caption" display="block" mt={1}>{file.name}</Typography>}
                </Box>

                {busy && <LinearProgress sx={{ mb: 2 }} />}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {result && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Importação concluída: {result.workstreams_created} frente(s) nova(s), {result.workstreams_updated} atualizada(s);
                        {' '}{result.activities_created} atividade(s) criada(s) e {result.activities_updated} atualizada(s);
                        {' '}{result.tasks_created} tarefa(s);
                        {' '}{result.baselines} linha(s) de base guardada(s).
                    </Alert>
                )}
                {result && (result.people_not_found || []).length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Pessoas da planilha que não estão alocadas no projeto: {result.people_not_found!.join(', ')}. As atividades
                        ficaram sem esse responsável e as tarefas, sem responsável (o nome da planilha ficou na descrição).
                        Aloque as pessoas nas posições (aba Equipe) e reimporte, ou ajuste na tela.
                    </Alert>
                )}
                {result && (result.owners_not_found || []).length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Owners da planilha que não são usuários do projeto: {result.owners_not_found!.join(', ')}. As frentes
                        ficaram sem owner — inclua a pessoa na equipe (com usuário) ou aloque-a e defina o owner em "Editar frente".
                    </Alert>
                )}

                {sheets.length > 0 && !result && (
                    <>
                        <TableContainer sx={{ mb: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" />
                                        <TableCell>Aba</TableCell>
                                        <TableCell>Tipo</TableCell>
                                        <TableCell>Conteúdo</TableCell>
                                        <TableCell sx={{ minWidth: 180 }}>Frente no sistema</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sheets.map((s) => {
                                        const importable = IMPORTABLE.includes(s.kind);
                                        const state = selected[s.title];
                                        return (
                                            <TableRow key={s.title} sx={{ opacity: importable ? 1 : 0.6 }}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox checked={!!state?.checked} disabled={!importable}
                                                        inputProps={{ 'aria-label': `Importar aba ${s.title}` }}
                                                        onChange={(e) => setSelected({ ...selected, [s.title]: { ...state, checked: e.target.checked } })} />
                                                </TableCell>
                                                <TableCell>
                                                    {s.title}
                                                    {s.report_date && <Typography variant="caption" display="block" color="text.secondary">Data ref.: {formatDate(s.report_date)}</Typography>}
                                                </TableCell>
                                                <TableCell><Chip size="small" label={KIND_LABELS[s.kind]} /></TableCell>
                                                <TableCell>
                                                    <Typography variant="caption">
                                                        {s.kind === 'schedule' && `${s.phases.length} fases, ${s.activities} atividades${s.notes ? `, ${s.notes} anotação(ões) → tarefas` : ''}`}
                                                        {s.kind === 'text' && `${s.notes} linha(s) de texto`}
                                                        {s.kind === 'daily' && `${s.tasks} tarefa(s)`}
                                                        {s.kind === 'executive' && 'Recalculada pelo sistema (não importada)'}
                                                    </Typography>
                                                    {s.warnings.map((w) => <Typography key={w} variant="caption" color="warning.main" display="block">{w}</Typography>)}
                                                </TableCell>
                                                <TableCell>
                                                    {importable && s.kind !== 'daily' ? (
                                                        <TextField size="small" fullWidth value={state?.name || ''} disabled={!state?.checked}
                                                            inputProps={{ 'aria-label': `Frente para a aba ${s.title}` }}
                                                            onChange={(e) => setSelected({ ...selected, [s.title]: { ...state, name: e.target.value } })} />
                                                    ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {duplicated.length > 0 && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Duas abas marcadas vão para a mesma frente ({Array.from(new Set(duplicated)).join(', ')}). Desmarque uma
                                (ex.: a versão antiga) ou dê nomes diferentes.
                            </Alert>
                        )}

                        <Stack spacing={1}>
                            <Typography variant="subtitle2">Se a frente já existir</Typography>
                            <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as 'update' | 'replace')}>
                                <FormControlLabel value="update" control={<Radio />}
                                    label="Atualizar: casa as atividades pelo nome, atualiza % e datas e mantém o que só existe no sistema" />
                                <FormControlLabel value="replace" control={<Radio />}
                                    label="Substituir: recria o cronograma da frente igual à planilha" />
                            </RadioGroup>
                            <FormControlLabel control={<Switch checked={importTasks} onChange={(e) => setImportTasks(e.target.checked)} />}
                                label="Importar tarefas (aba Daily e anotações das abas)" />
                        </Stack>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={close}>{result ? 'Fechar' : 'Cancelar'}</Button>
                {!result && (
                    <Button variant="contained" onClick={runImport}
                        disabled={busy || !file || chosenSheets.length === 0 || duplicated.length > 0}>
                        Importar {chosenSheets.length ? `(${chosenSheets.length} aba${chosenSheets.length > 1 ? 's' : ''})` : ''}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ImportDialog;
